import apiClient from '@/services/apiClient';

export function getUser() {
  try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
}
const MANAGER_ROLES = ['admin', 'super admin', 'super_admin', 'site admin', 'transport manager', 'supervisor', 'floor supervisor', 'section incharge', 'incharge', 'floor incharge'];
export function isManager() {
  const r = (getUser().role || '').toLowerCase();
  return MANAGER_ROLES.includes(r);
}
export function isDriver() {
  return (getUser().role || '').toLowerCase() === 'driver';
}

export function getGPS(timeout = 8000) {
  const once = (opts) => new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null),
      opts
    );
  });
  if (!navigator.geolocation) return Promise.resolve(null);
  // precise fix first; fall back to a recent / network fix so slow GPS doesn't fail
  return once({ enableHighAccuracy: true, timeout, maximumAge: 0 })
    .then((g) => g || once({ enableHighAccuracy: false, timeout: 10000, maximumAge: 120000 }));
}

export async function downloadCsv(path, filename) {
  const res = await apiClient.get(path, { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement('a');
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  a.remove(); window.URL.revokeObjectURL(url);
}

const T = '/transport';
export const transportApi = {
  getSettings: () => apiClient.get(`${T}/settings`).then(r => r.data),
  saveSettings: (b) => apiClient.put(`${T}/settings`, b).then(r => r.data),
  dashboard: () => apiClient.get(`${T}/dashboard`).then(r => r.data),
  listSiteSections: () => apiClient.get('/internal-transfer/sections').then(r => (r.data.sections || []).map(s => s.section_name)),

  listVehicles: (params) => apiClient.get(`${T}/vehicles`, { params }).then(r => r.data.vehicles),
  createVehicle: (b) => apiClient.post(`${T}/vehicles`, b).then(r => r.data),
  updateVehicle: (id, b) => apiClient.put(`${T}/vehicles/${id}`, b).then(r => r.data),
  deleteVehicle: (id) => apiClient.delete(`${T}/vehicles/${id}`).then(r => r.data),

  listDrivers: (params) => apiClient.get(`${T}/drivers`, { params }).then(r => r.data.drivers),
  createDriver: (b) => apiClient.post(`${T}/drivers`, b).then(r => r.data),
  updateDriver: (id, b) => apiClient.put(`${T}/drivers/${id}`, b).then(r => r.data),
  deleteDriver: (id) => apiClient.delete(`${T}/drivers/${id}`).then(r => r.data),

  listVendors: () => apiClient.get(`${T}/vendors`).then(r => r.data.vendors),
  createVendor: (name) => apiClient.post(`${T}/vendors`, { name }).then(r => r.data),

  uploadPhoto: (image_base64, folder) => apiClient.post(`${T}/upload-photo`, { image_base64, folder }).then(r => r.data),
  readOdometer: (image_base64, vehicle_id, context) => apiClient.post(`${T}/odometer/read`, { image_base64, vehicle_id, context }).then(r => r.data),
  odoLink: (tripId) => apiClient.get(`${T}/trips/${tripId}/odo-link`).then(r => r.data),
  fixOdometer: (tripId, b) => apiClient.post(`${T}/trips/${tripId}/odometer-fix`, b).then(r => r.data),

  listTrips: (params) => apiClient.get(`${T}/trips`, { params }).then(r => r.data.trips),
  createTrip: (b) => apiClient.post(`${T}/trips`, b).then(r => r.data),
  startTrip: (id, b) => apiClient.post(`${T}/trips/${id}/start`, b).then(r => r.data),
  readyToLoad: (id, b) => apiClient.post(`${T}/trips/${id}/ready-to-load`, b || {}).then(r => r.data),
  unloadingComplete: (id, b) => apiClient.post(`${T}/trips/${id}/unloading-complete`, b || {}).then(r => r.data),
  endTrip: (id, b) => apiClient.post(`${T}/trips/${id}/end`, b).then(r => r.data),
  deliverTrip: (id, b) => apiClient.post(`${T}/trips/${id}/deliver`, b).then(r => r.data),
  cancelTrip: (id, reason) => apiClient.post(`${T}/trips/${id}/cancel`, { reason }).then(r => r.data),
  addFuelPhotos: (id, b) => apiClient.post(`${T}/fuel/${id}/photos`, b).then(r => r.data),
  addServicePhotos: (id, b) => apiClient.post(`${T}/service/${id}/photos`, b).then(r => r.data),

  listFuel: (params) => apiClient.get(`${T}/fuel`, { params }).then(r => r.data.fuel),
  createFuel: (b) => apiClient.post(`${T}/fuel`, b).then(r => r.data),
  listService: (params) => apiClient.get(`${T}/service`, { params }).then(r => r.data.service),
  createService: (b) => apiClient.post(`${T}/service`, b).then(r => r.data),

  checkin: (b) => apiClient.post(`${T}/attendance/checkin`, b).then(r => r.data),
  recheckin: (b) => apiClient.post(`${T}/attendance/recheckin`, b).then(r => r.data),
  checkout: (b) => apiClient.post(`${T}/attendance/checkout`, b).then(r => r.data),
  listAttendance: (params) => apiClient.get(`${T}/attendance`, { params }).then(r => r.data.attendance),
  attendanceMonthly: (month) => apiClient.get(`${T}/attendance/monthly`, { params: { month } }).then(r => r.data),
  myIp: () => apiClient.get(`${T}/my-ip`).then(r => r.data),
  myAttendanceToday: () => apiClient.get(`${T}/attendance/me/today`).then(r => r.data),

  createRequest: (b) => apiClient.post(`${T}/requests`, b).then(r => r.data),
  listRequests: (params) => apiClient.get(`${T}/requests`, { params }).then(r => r.data.requests),
  assignRequest: (id, b) => apiClient.post(`${T}/requests/${id}/assign`, b).then(r => r.data),
  rejectRequest: (id, reason) => apiClient.post(`${T}/requests/${id}/reject`, { reason }).then(r => r.data),
  replaceRequestStops: (id, stops) => apiClient.put(`${T}/requests/${id}/stops`, { stops }).then(r => r.data),
  clubRequests: (b) => apiClient.post(`${T}/requests/club`, b).then(r => r.data),
  clubSuggestions: () => apiClient.get(`${T}/requests/club-suggestions`).then(r => r.data.suggestions || {}),
  unclubRequest: (id) => apiClient.post(`${T}/requests/${id}/unclub`).then(r => r.data),
  arriveStop: (tripId, stopId, b) => apiClient.post(`${T}/trips/${tripId}/stops/${stopId}/arrive`, b || {}).then(r => r.data),
  doneStop: (tripId, stopId, b) => apiClient.post(`${T}/trips/${tripId}/stops/${stopId}/done`, b || {}).then(r => r.data),

  myTasks: () => apiClient.get(`${T}/my-tasks`).then(r => r.data),
  runningTrips: () => apiClient.get(`${T}/running-trips`).then(r => r.data.trips),
  addLiveStop: (tripId, b) => apiClient.post(`${T}/trips/${tripId}/stops/add`, b).then(r => r.data),
  acceptLiveStop: (tripId, stopId) => apiClient.post(`${T}/trips/${tripId}/stops/${stopId}/accept`, {}).then(r => r.data),
  rejectLiveStop: (tripId, stopId, b) => apiClient.post(`${T}/trips/${tripId}/stops/${stopId}/reject`, b).then(r => r.data),
  emptyRunOptions: (source_trip_id, direction) => apiClient.get(`${T}/my-tasks/empty-run-options`, { params: { source_trip_id, direction } }).then(r => r.data),
  createEmptyRun: (b) => apiClient.post(`${T}/my-tasks/empty-run`, b).then(r => r.data),

  // Smart dispatch (Phase 2) — locations, geocoding, estimate, map, suggestion
  listLocations: (params) => apiClient.get(`${T}/locations`, { params }).then(r => r.data.locations),
  createLocation: (b) => apiClient.post(`${T}/locations`, b).then(r => r.data),
  updateLocation: (id, b) => apiClient.put(`${T}/locations/${id}`, b).then(r => r.data),
  deleteLocation: (id) => apiClient.delete(`${T}/locations/${id}`).then(r => r.data),
  geocode: (q) => apiClient.get(`${T}/geocode`, { params: { q } }).then(r => r.data.results),
  estimateDistance: (params) => apiClient.get(`${T}/estimate-distance`, { params }).then(r => r.data),
  dispatchMap: () => apiClient.get(`${T}/dispatch-map`).then(r => r.data),
  backhaulSuggestions: () => apiClient.get(`${T}/backhaul-suggestions`).then(r => r.data.returning),
  requestSuggestion: (id, pairing) => apiClient.get(`${T}/requests/${id}/suggestion`, { params: pairing ? { pairing } : {} }).then(r => r.data),
  pingTrip: (id, body) => apiClient.post(`${T}/trips/${id}/ping`, body).then(r => r.data),
  tripTrack: (id) => apiClient.get(`${T}/trips/${id}/track`).then(r => r.data),

  reportDayWise: (params) => apiClient.get(`${T}/reports/day-wise`, { params }).then(r => r.data),
  reportVehicleWise: (params) => apiClient.get(`${T}/reports/vehicle-wise`, { params }).then(r => r.data),
  reportDriverRanking: (params) => apiClient.get(`${T}/reports/driver-ranking`, { params }).then(r => r.data),
  reportRouteDeviation: (params) => apiClient.get(`${T}/reports/route-deviation`, { params }).then(r => r.data),
  reportLoadUnload: (params) => apiClient.get(`${T}/reports/load-unload`, { params }).then(r => r.data),
  reportFuelEfficiency: (params) => apiClient.get(`${T}/reports/fuel-efficiency`, { params }).then(r => r.data),
  reportMonthlyTrend: (params) => apiClient.get(`${T}/reports/monthly-trend`, { params }).then(r => r.data),
  reportDriverMileage: (params) => apiClient.get(`${T}/reports/driver-mileage`, { params }).then(r => r.data),

  // user list (reused from messenger) to link drivers to login accounts
  listUsers: () => apiClient.get('/messenger/users').then(r => r.data.users || r.data),
};

export function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
